import React from 'react'
import { useTranslation } from 'react-i18next'

import { AttentionIcon, SearchIcon, type IconProps } from '../icons'

import { InfoMessageBold } from './info-message-bold'
import { InfoMessageDt } from './info-message-dt'

import type { ClassNameProps } from '@root/shared/react'

export type InfoMessageVariant = 'dt' | 'bold'
export type InfoIntent = 'error' | 'empty'

export type InfoMessageProps = {
  intent: InfoIntent
  variant?: InfoMessageVariant
  title?: React.ReactNode
  description?: React.ReactNode
  retryText?: React.ReactNode
  onRetry?: () => void
  icon?: React.ReactElement<IconProps>
  hint?: React.ReactNode
  children?: React.ReactNode
  withBackground?: boolean
  withSeparator?: boolean
  iconSize?: number
} & ClassNameProps

export const InfoMessage: React.FC<InfoMessageProps> = ({
  variant = 'dt',
  title,
  intent,
  description,
  retryText,
  icon,
  withBackground = false,
  iconSize = 28,
  ...props
}) => {
  const { t } = useTranslation()

  const resolvedDescription = (() => {
    if (description) {
      return description
    }
    if (intent === 'empty') {
      return t('common.no-items')
    }
    return t('common.something_went_wrong_try_again')
  })()

  const resolvedTitle = (() => {
    if (title) {
      return title
    }
    if (intent === 'empty') {
      return t('common.empty')
    }
    return t('common.loading-error')
  })()

  const resolvedIcon = (() => {
    if (intent === 'error') {
      return <AttentionIcon size={iconSize} />
    }

    const resolvedIcon = icon ?? <SearchIcon size={iconSize} />
    const iconElement = React.cloneElement(resolvedIcon, { size: iconSize })

    return iconElement
  })()

  const Cmp = variant === 'dt' ? InfoMessageDt : InfoMessageBold

  let resolvedRetryText = retryText
  if (!resolvedRetryText) {
    resolvedRetryText =
      intent === 'error' ? t('common.retry') : t('common.refresh')
  }

  return (
    <Cmp
      {...props}
      intent={intent}
      icon={resolvedIcon}
      title={resolvedTitle}
      description={resolvedDescription}
      retryText={resolvedRetryText}
      withBackground={withBackground}
    />
  )
}
